import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSnackbar } from '../components/Snackbar';
import { authAPI } from '../services/api';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkOutlineIcon from '@mui/icons-material/WorkOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import './Profile.css';

const TABS = [
  { key: 'profile',     label: 'Profile',     Icon: PersonIcon },
  // { key: 'security',    label: 'Security',    Icon: LockOutlinedIcon },
  { key: 'preferences', label: 'Preferences', Icon: TuneIcon },
];

const PasswordField = ({ label, value, onChange, show, onToggleShow }) => (
  <div className="pp-pw-field">
    <label className="pp-pw-lbl">{label}</label>
    <div className="pp-pw-input-wrap">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        className="pp-pw-input"
        autoComplete="new-password"
      />
      <button type="button" className="pp-pw-eye" onClick={onToggleShow} tabIndex={-1}>
        {show ? <VisibilityOffIcon sx={{ fontSize: 17 }} /> : <VisibilityIcon sx={{ fontSize: 17 }} />}
      </button>
    </div>
  </div>
);

const SecurityTab = () => {
  const snack = useSnackbar();
  const [current, setCurrent]   = useState('');
  const [next, setNext]         = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showC, setShowC] = useState(false);
  const [showN, setShowN] = useState(false);
  const [showX, setShowX] = useState(false);
  const [saving, setSaving] = useState(false);

  const canSubmit = current.length > 0 && next.length >= 8 && next === confirm && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return snack('New password and confirmation do not match', 'error');
    if (next.length < 3) return snack('New password must be at least 3 characters', 'error');

    setSaving(true);
    try {
      await authAPI.changePassword({ currentPassword: current, newPassword: next });
      snack('Password updated successfully', 'success');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      snack(err.response?.data?.error || 'Failed to update password', 'error');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); };

  return (
    <>
      <div className="pp-hero">
        <div className="pp-hero-icon"><LockOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} /></div>
        <div>
          <div className="pp-hero-title">Change Password</div>
          <div className="pp-hero-sub">Keep your account secure with a strong password</div>
        </div>
      </div>

      <div className="pp-card">
        <form className="pp-pw-form" onSubmit={submit}>
          <PasswordField label="Old Password" value={current} onChange={e => setCurrent(e.target.value)} show={showC} onToggleShow={() => setShowC(s => !s)} />
          <PasswordField label="New Password" value={next} onChange={e => setNext(e.target.value)} show={showN} onToggleShow={() => setShowN(s => !s)} />
          <PasswordField label="Confirm New Password" value={confirm} onChange={e => setConfirm(e.target.value)} show={showX} onToggleShow={() => setShowX(s => !s)} />
          <div className="pp-pw-hint">Must be at least 8 characters.</div>
          <div className="pp-pw-actions">
            <button type="submit" className="pp-pw-submit" disabled={!canSubmit}>
              {saving ? 'Updating…' : 'Update Password'}
            </button>
            <button type="button" className="pp-pw-cancel" onClick={reset} disabled={saving}>Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
};

const PreferencesTab = () => {
  const { dark, toggle } = useTheme();
  return (
    <>
      <div className="pp-hero">
        <div className="pp-hero-icon"><SettingsIcon sx={{ fontSize: 20, color: '#fff' }} /></div>
        <div>
          <div className="pp-hero-title">App Preferences</div>
          <div className="pp-hero-sub">Customize your app experience</div>
        </div>
      </div>

      <div className="pp-card pp-toggle-row">
        <div>
          <div className="pp-toggle-title">Dark Mode</div>
          <div className="pp-toggle-sub">Switch between light and dark themes</div>
        </div>
        <button
          type="button"
          className={`pp-switch ${dark ? 'on' : ''}`}
          role="switch"
          aria-checked={dark}
          aria-label="Toggle dark mode"
          onClick={toggle}
        >
          <span className="pp-switch-knob" />
        </button>
      </div>
    </>
  );
};

const Profile = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('profile');

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase() || '?';
  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Unnamed user';
  const role     = user?.role || 'user';
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div className="profile-page">
      <div className="pp-header">
        <h1 className="page-title" style={{ marginBottom: 4 }}>Settings</h1>
        <p className="pp-sub">Manage your account and application preferences</p>
      </div>

      <div className="pp-layout">
        <div className="pp-nav">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} className={`pp-nav-item ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>
              {tab === key ? <ChevronRightIcon sx={{ fontSize: 16 }} /> : <span className="pp-nav-spacer" />}
              <span>{label}</span>
            </button>
          ))}
        </div>

        <div className="pp-content">
          {tab === 'profile' ? (
            <>
              <div className="pp-hero">
                <div className="pp-hero-icon"><PersonIcon sx={{ fontSize: 20, color: '#fff' }} /></div>
                <div>
                  <div className="pp-hero-title">Profile Settings</div>
                  <div className="pp-hero-sub">Manage your personal information and preferences</div>
                </div>
              </div>

              <div className="pp-card">
                <div className="pp-card-title">Personal Information</div>
                <div className="pp-who">
                  <div className="pp-avatar">{initials}</div>
                  <div>
                    <div className="pp-who-name">{fullName}</div>
                    {/* <div className="pp-who-role">{role.toUpperCase()}</div>
                    <div className="pp-who-email">{user?.email}</div> */}
                  </div>
                </div>

                <div className="pp-grid">
                  <div className="pp-field">
                    <EmailIcon sx={{ fontSize: 17 }} />
                    <div>
                      <div className="pp-field-lbl">Email Address</div>
                      <div className="pp-field-val">{user?.email || 'Not provided'}</div>
                    </div>
                  </div>
                  <div className="pp-field">
                    <CalendarTodayIcon sx={{ fontSize: 17 }} />
                    <div>
                      <div className="pp-field-lbl">Member Since</div>
                      <div className="pp-field-val">{memberSince}</div>
                    </div>
                  </div>
                  {/* <div className="pp-field">
                    <PhoneIcon sx={{ fontSize: 17 }} />
                    <div>
                      <div className="pp-field-lbl">Phone Number</div>
                      <div className="pp-field-val na">Not provided</div>
                    </div>
                  </div> */}
                  {/* <div className="pp-field">
                    <BadgeIcon sx={{ fontSize: 17 }} />
                    <div>
                      <div className="pp-field-lbl">Employee ID</div>
                      <div className="pp-field-val">{user?.emp_id || 'Not provided'}</div>
                    </div>
                  </div> */}
                </div>
              </div>

              <div className="pp-card">
                <div className="pp-card-title">Professional Information</div>
                <div className="pp-grid">
                  <div className="pp-field">
                    <WorkOutlineIcon sx={{ fontSize: 17 }} />
                    <div>
                      <div className="pp-field-lbl">Role</div>
                      <div className="pp-field-val">{role.toUpperCase()}</div>
                    </div>
                  </div>
                  {/* <div className="pp-field">
                    <BadgeIcon sx={{ fontSize: 17 }} />
                    <div>
                      <div className="pp-field-lbl">Department</div>
                      <div className="pp-field-val na">Not provided</div>
                    </div>
                  </div> */}
                </div>
              </div>
            </>
          ) : tab === 'security' ? (
            <SecurityTab />
          ) : (
            <PreferencesTab />
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;